import React, { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { useRights } from "../../../hooks/rights"
import CollapsibleCardV2 from "../../basics/interface/CollapsibleCardV2"
import {
	fetchCouverturesFiche,
	fetchCouverturesMission,
} from "../../../store/actions/couverturesActions"
import {
	AssistancesProvider,
	useAssistances,
} from "../assistances/AssistancesContext"
import {
	OrdresAssistance,
	useFicheAssistance,
} from "../assistances/FicheAssistanceBloc"
import MessageModal from "../assistances/MessageModal"
import { URL } from "../../../store/actions/depannageActions"
import CouvertureFormModal from "./CouvertureFormModal"
import EncaisserCouvertureModal from "./EncaisserCouvertureModal"
import SelecteurCibleModal from "./SelecteurCibleModal"
import {
	NouvelleInterventionModal,
	StatutCouvertureModal,
} from "./CouvertureActionsModals"
import {
	Badge,
	NonConfirmee,
	SOURCE_ICONE,
	SOURCE_TEXTE,
	StatutBadge,
	chf,
	ui,
	useEtroit,
} from "./couverturesUi"

// Couvertures d'assistance (GE-60, spec §6.1 / §9, maquette validée
// docs/couvertures/maquette-couvertures.html) :
// - fiche dépannage : carte « Couvertures » AU-DESSUS de Trajet, pour les 3
//   types d'appel, même vide ; elle remplace le bloc « Assistance » GE-57 et
//   en reprend les ordres ;
// - fenêtre de mission (avant conversion) : section compacte, même composant
//   (`compact`), sans encaissement (il se fait sur le terrain, C4).
// Données : api/assistances/couvertures/{fiche|mission}/<id> (calculs
// serveur : utilisé, disponible, proposé, reste client).

export const ANCRE_COUVERTURES = "couvertures"

// Chargement + rechargement à la demande (`cle` : change quand les
// encaissements sauvegardés de la fiche bougent).
export const useCouvertures = ({
	depId = null,
	missionId = null,
	cle = "",
}) => {
	const dispatch = useDispatch()
	const [data, setData] = useState(null)
	const [revision, setRevision] = useState(0)
	useEffect(() => {
		let actif = true
		if (!depId && !missionId) {
			setData(null)
			return undefined
		}
		const action = depId
			? fetchCouverturesFiche(depId)
			: fetchCouverturesMission(missionId)
		dispatch(action).then((d) => {
			if (!actif) return
			// Revue 08.10 (I9) : un échec ne laisse plus « Chargement… ».
			setData(d || { couvertures: [], calcul: {}, erreur: true })
		})
		return () => {
			actif = false
		}
	}, [dispatch, depId, missionId, cle, revision])
	// Feuilletage : pas de données de la fiche précédente.
	useEffect(() => setData(null), [depId, missionId])
	return {
		data,
		setData,
		recharger: () => setRevision((r) => r + 1),
	}
}

// Sous-ligne du bandeau « Reste à payer » de la fiche : « dont couvert par
// Mobi24 : 540.– · reste client : 0.– », cliquable (défile jusqu'à la carte).
export const ligneCouvert = (data) => {
	if (!data || !data.couvertures) return null
	const actives = data.couvertures.filter((c) => c.statut === "active")
	if (!actives.length) return null
	const noms = [...new Set(actives.map((c) => c.assistance))].join(", ")
	const precise = actives.some((c) => c.plafond != null)
	const calcul = data.calcul || {}
	return {
		texte: precise
			? `dont couvert par ${noms} : ${chf(calcul.couvert, "0.–")} · reste client : ${chf(calcul.reste_client, "0.–")}`
			: `dont couvert par ${noms} : montant non précisé`,
		resteClient: calcul.reste_client,
		partiel: !!calcul.partiel,
		precise,
	}
}

// Proposition appliquée / dossier modifié depuis le panneau (revision du
// contexte GE-57) → la carte et le bandeau relisent (revue 08.10, I11).
const SuiviRevision = ({ onChange }) => {
	const ctx = useAssistances()
	const revision = ctx ? ctx.revision : 0
	const premier = useRef(true)
	useEffect(() => {
		if (premier.current) {
			premier.current = false
			return
		}
		onChange()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [revision])
	return null
}

export const defilerVersCouvertures = () => {
	const el = document.getElementById(ANCRE_COUVERTURES)
	if (el && el.scrollIntoView)
		el.scrollIntoView({ behavior: "smooth", block: "start" })
}

const CouverturesCarte = ({
	depId = null,
	missionId = null,
	// Fiche : { data, setData, recharger } de useCouvertures (partagé avec le
	// bandeau) ; mission : chargé ici.
	etat = null,
	compact = false,
	sousTitre = "",
	lectureSeule = false,
	open,
	onToggle,
	// Fiche : (réponse serveur) après un encaissement → transactions fusionnées.
	onEncaisse = null,
}) => {
	const interne = useCouvertures({ missionId: etat ? null : missionId })
	const { data, setData, recharger } = etat || interne
	const { hasRight } = useRights()
	const peutGerer = hasRight("dep_dispatch") && !lectureSeule
	const etroit = useEtroit()
	const [modal, setModal] = useState(null)

	const ouvrir = (type, extra = {}) => setModal({ type, ...extra })
	const fermer = () => setModal(null)
	const apresAction = (res) => {
		fermer()
		if (res && res.couvertures) setData(res)
		else recharger()
	}

	const couvertures = (data && data.couvertures) || []
	const courantes = couvertures.filter((c) =>
		["active", "proposee"].includes(c.statut),
	)
	const historique = couvertures.filter(
		(c) => !["active", "proposee"].includes(c.statut),
	)
	const calcul = (data && data.calcul) || {}
	const rattachement = depId
		? { depannage_id: depId }
		: { dep_mission_id: missionId }
	// Dernière couverture active avec plafond : porte le « reste client ».
	const derniereAvecPlafond = [...courantes]
		.reverse()
		.find((c) => c.statut === "active" && c.plafond != null)

	const bouton = peutGerer ? (
		<button
			type="button"
			style={ui.btn("", true)}
			onClick={() => ouvrir("form")}
		>
			+ Couverture
		</button>
	) : null

	const contenu = (
		<>
			{data === null ? (
				<div style={st.empty}>Chargement…</div>
			) : data.erreur ? (
				<div style={{ ...st.empty, color: COLORS.danger }}>
					Impossible de charger les couvertures.{" "}
					{/* Lien (pas un contrôle de formulaire) : actif dans le
					    fieldset désactivé d'une fiche en lecture seule. */}
					<a
						href="#reessayer"
						role="button"
						style={{ ...ui.lien, textDecoration: "none" }}
						onClick={(e) => {
							e.preventDefault()
							recharger()
						}}
					>
						Réessayer
					</a>
				</div>
			) : !courantes.length ? (
				<div style={st.empty}>
					Aucune couverture
					{peutGerer
						? " — « + Couverture » pour en saisir une."
						: "."}
				</div>
			) : (
				courantes.map((c, i) => (
					<LigneCouverture
						key={`cv_${c.id}`}
						c={c}
						derniere={i === courantes.length - 1}
						compact={compact}
						etroit={etroit}
						peutGerer={peutGerer}
						calcul={calcul}
						afficheReste={
							derniereAvecPlafond &&
							derniereAvecPlafond.id === c.id
						}
						encaissable={
							!!depId &&
							!lectureSeule &&
							!(data.fiche && data.fiche.close)
						}
						ouvrir={ouvrir}
					/>
				))
			)}
			{historique.length > 0 && (
				<details style={st.histo}>
					<summary style={{ cursor: "pointer" }}>
						Historique ({historique.length} couverture
						{historique.length > 1 ? "s" : ""}{" "}
						{resumeHistorique(historique)})
					</summary>
					{historique.map((c) => (
						<div
							key={`h_${c.id}`}
							style={{ ...st.cov, opacity: 0.6 }}
						>
							<div style={st.r1}>
								<span style={st.who}>{c.assistance}</span>
								<span style={ui.mono}>
									{[c.ref_dossier, c.ref_ordre]
										.filter(Boolean)
										.join(" / ") || "—"}
								</span>
								<span style={st.src}>
									{SOURCE_ICONE[c.source]}{" "}
									{c.cree_le.slice(0, 5)}
								</span>
								<StatutBadge c={c} />
							</div>
						</div>
					))}
				</details>
			)}
			{depId && !compact && <OrdresFiche depId={depId} />}
		</>
	)

	const modals = (
		<>
			{modal && modal.type === "form" && (
				<CouvertureFormModal
					rattachement={rattachement}
					sousTitre={sousTitre}
					couverture={modal.couverture || null}
					onClose={fermer}
					onSaved={apresAction}
					onRequalifier={
						depId
							? (mid) =>
									setModal({
										type: "requalifier",
										missionId: mid,
									})
							: null
					}
				/>
			)}
			{modal && modal.type === "requalifier" && (
				<SelecteurCibleModal
					mode="couverture"
					missionId={modal.missionId}
					ciblePreselection={`fiche:${depId}`}
					onClose={fermer}
					onDone={() => apresAction(null)}
				/>
			)}
			{modal && modal.type === "deplacer" && (
				<SelecteurCibleModal
					mode="deplacer"
					couvertureId={modal.couverture.id}
					onClose={fermer}
					onDone={() => apresAction(null)}
				/>
			)}
			{modal && modal.type === "nouvelle" && (
				<NouvelleInterventionModal
					couverture={modal.couverture}
					sousTitre={`Couverture ${modal.couverture.assistance}${
						modal.couverture.ref_dossier
							? ` ${modal.couverture.ref_dossier}`
							: ""
					}${depId ? ` · fiche ${depId}` : ""}`}
					onClose={fermer}
					onDone={() => apresAction(null)}
				/>
			)}
			{modal && modal.type === "statut" && (
				<StatutCouvertureModal
					couverture={modal.couverture}
					action={modal.action}
					sousTitre={`${modal.couverture.assistance}${
						modal.couverture.ref_dossier
							? ` · ${modal.couverture.ref_dossier}`
							: ""
					}`}
					onClose={fermer}
					onDone={apresAction}
				/>
			)}
			{modal && modal.type === "encaisser" && (
				<EncaisserCouvertureModal
					couverture={modal.couverture}
					ficheId={depId}
					reste={calcul.reste_a_payer}
					onClose={fermer}
					onDone={(res) => {
						apresAction(res)
						if (onEncaisse) onEncaisse(res)
					}}
				/>
			)}
			{modal && modal.type === "mail" && (
				<MessageModal messageId={modal.messageId} onClose={fermer} />
			)}
		</>
	)

	if (compact)
		return (
			<div style={st.compact}>
				<div style={st.compactTitre}>
					<span>Couvertures</span>
					{bouton}
				</div>
				{contenu}
				{modals}
			</div>
		)

	return (
		<AssistancesProvider>
			<SuiviRevision onChange={recharger} />
			<div id={ANCRE_COUVERTURES} style={{ scrollMarginTop: 80 }}>
				<CollapsibleCardV2
					title="Couvertures"
					right={bouton}
					open={open}
					onToggle={onToggle}
				>
					{contenu}
				</CollapsibleCardV2>
			</div>
			{modals}
		</AssistancesProvider>
	)
}

const resumeHistorique = (liste) => {
	const conv = liste.filter((c) => c.statut === "convertie").length
	const ann = liste.length - conv
	return [
		conv ? `${conv > 1 ? "converties" : "convertie"}` : null,
		ann ? `${ann > 1 ? "annulées" : "annulée"}` : null,
	]
		.filter(Boolean)
		.join(" / ")
}

// Ordres GE-57 des dossiers de la fiche (ex-bloc « Assistance »).
const OrdresFiche = ({ depId }) => {
	const data = useFicheAssistance(depId)
	if (!data || !data.dossiers || !data.dossiers.length) return null
	return (
		<div style={st.ordres}>
			<div style={st.sousTitre}>Ordres d'assistance</div>
			<OrdresAssistance data={data} />
		</div>
	)
}

const LigneCouverture = ({
	c,
	derniere,
	compact,
	etroit,
	peutGerer,
	calcul,
	afficheReste,
	encaissable,
	ouvrir,
}) => {
	const refs = [c.ref_dossier, c.ref_ordre].filter(Boolean).join(" / ")
	const source = [
		`${SOURCE_ICONE[c.source] || ""} ${SOURCE_TEXTE[c.source] || ""}`.trim(),
		c.cree_le,
		c.interlocuteur,
	]
		.filter(Boolean)
		.join(" · ")
	const nonConfirmee =
		c.statut === "active" &&
		!c.confirmee &&
		["telephone", "manuel"].includes(c.source)
	const pct = c.plafond
		? Math.min(100, Math.round((100 * (c.utilise || 0)) / c.plafond))
		: 0
	const reste = calcul.reste_client || 0
	const encaisserMontant = c.propose
	return (
		<div style={{ ...st.cov, ...(derniere ? st.covDerniere : null) }}>
			<div style={st.r1}>
				<span style={st.who}>{c.assistance}</span>
				<span style={ui.mono}>{refs || "—"}</span>
				<span style={st.src}>{source}</span>
				<StatutBadge c={c} />
				{nonConfirmee && <NonConfirmee />}
				{c.ordre_annule && (
					<Badge color={COLORS.danger} bg={COLORS.dangerLight}>
						⚠ Ordre annulé
					</Badge>
				)}
				<span
					style={{
						...st.plafond,
						...(etroit ? { marginLeft: 0, width: "100%" } : null),
					}}
				>
					{c.plafond != null ? (
						<>
							jusqu'à{" "}
							<b style={{ color: COLORS.text, fontSize: 15 }}>
								{chf(c.plafond)}
							</b>{" "}
							TTC
						</>
					) : (
						"montant non précisé"
					)}
				</span>
				{peutGerer && (
					<MenuCouverture
						c={c}
						compact={compact}
						ouvrir={ouvrir}
						etroit={etroit}
					/>
				)}
			</div>
			{(c.perimetre ||
				c.facturation ||
				c.message_id ||
				c.justificatif ||
				nonConfirmee) && (
				<div style={st.r2}>
					{c.perimetre && <span>Périmètre : {c.perimetre}</span>}
					{nonConfirmee && !c.facturation && (
						<span>
							{c.cree_par ? `Saisie par ${c.cree_par} — ` : ""}en
							attente de confirmation écrite
						</span>
					)}
					{c.facturation && <span>{c.facturation}</span>}
					{c.message_id ? (
						// Lien (et pas bouton) : reste actif dans le fieldset
						// désactivé d'une fiche en lecture seule (revue 08.10).
						<a
							href="#voir-email"
							role="button"
							style={{ ...ui.lien, textDecoration: "none" }}
							onClick={(e) => {
								e.preventDefault()
								ouvrir("mail", { messageId: c.message_id })
							}}
						>
							voir l'e-mail
						</a>
					) : c.justificatif ? (
						<a
							href={lienMedia(c.justificatif)}
							target="_blank"
							rel="noopener noreferrer"
							style={{ ...ui.lien, textDecoration: "none" }}
						>
							voir le justificatif
						</a>
					) : null}
				</div>
			)}
			{c.statut === "proposee" && (
				<div style={st.valider}>
					<span>
						⚠ Couverture reçue par e-mail : à valider avant de
						compter dans les totaux.
					</span>
					<span style={{ flex: 1 }} />
					{peutGerer && (
						<>
							<button
								type="button"
								style={ui.btn("", true)}
								onClick={() =>
									ouvrir("statut", {
										couverture: c,
										action: "rejeter",
									})
								}
							>
								Rejeter
							</button>
							<button
								type="button"
								style={ui.btn("primary", true)}
								onClick={() =>
									ouvrir("statut", {
										couverture: c,
										action: "valider",
									})
								}
							>
								Valider
							</button>
						</>
					)}
				</div>
			)}
			{c.statut === "active" && (c.plafond != null || encaissable) && (
				<div style={st.r3}>
					{c.plafond != null ? (
						<div style={st.jauge}>
							<div style={st.bar}>
								<div
									style={{
										height: "100%",
										width: `${pct}%`,
										backgroundColor: COLORS.success,
									}}
								/>
							</div>
							<div style={st.lab}>
								Encaissé{" "}
								<b style={{ color: COLORS.text }}>
									{chf(c.utilise, "0.–")}
								</b>{" "}
								sur{" "}
								<b style={{ color: COLORS.text }}>
									{chf(c.plafond)}
								</b>
								{afficheReste && !compact && (
									<>
										{" · "}
										{calcul.partiel ? (
											<span
												style={{
													color: COLORS.warning,
													fontWeight: 600,
												}}
											>
												reste client : {chf(reste)} à
												encaisser
											</span>
										) : (
											<span>
												reste client :{" "}
												{chf(reste, "0.–")}
											</span>
										)}
									</>
								)}
							</div>
						</div>
					) : (
						<div style={{ ...st.lab, flex: 1 }}>
							Montant non précisé : encaissement à saisir.
						</div>
					)}
					{encaissable &&
						(encaisserMontant === null ||
							encaisserMontant === undefined ||
							encaisserMontant > 0) && (
							<button
								type="button"
								style={ui.btn("primary")}
								onClick={() =>
									ouvrir("encaisser", { couverture: c })
								}
							>
								{encaisserMontant
									? `Encaisser ${chf(encaisserMontant)}`
									: "Encaisser…"}
							</button>
						)}
				</div>
			)}
		</div>
	)
}

const lienMedia = (chemin) =>
	/^https?:/.test(chemin) ? chemin : `${URL.replace(/\/$/, "")}${chemin}`

const MenuCouverture = ({ c, compact, ouvrir, etroit }) => {
	const [ouvert, setOuvert] = useState(false)
	const ref = useRef(null)
	useEffect(() => {
		if (!ouvert) return undefined
		const fermer = (e) => {
			if (ref.current && !ref.current.contains(e.target)) setOuvert(false)
		}
		const echap = (e) => {
			if (e.key === "Escape") setOuvert(false)
		}
		document.addEventListener("mousedown", fermer)
		document.addEventListener("keydown", echap)
		return () => {
			document.removeEventListener("mousedown", fermer)
			document.removeEventListener("keydown", echap)
		}
	}, [ouvert])
	const action =
		(type, extra = {}) =>
		() => {
			setOuvert(false)
			ouvrir(type, { couverture: c, ...extra })
		}
	return (
		<div ref={ref} style={{ position: "relative" }}>
			<button
				type="button"
				style={st.menuBtn}
				aria-label="Actions"
				aria-expanded={ouvert}
				onClick={() => setOuvert((o) => !o)}
			>
				⋯
			</button>
			{ouvert && (
				<div style={{ ...st.menu, ...(etroit ? { right: 0 } : null) }}>
					<button
						type="button"
						style={st.menuItem}
						onClick={action("form")}
					>
						Modifier…
					</button>
					<button
						type="button"
						style={st.menuItem}
						onClick={action("deplacer")}
					>
						{compact
							? "Déplacer vers une fiche…"
							: "Déplacer vers une autre fiche…"}
					</button>
					<button
						type="button"
						style={st.menuItem}
						onClick={action("nouvelle")}
					>
						C'est une nouvelle intervention…
					</button>
					<button
						type="button"
						style={{ ...st.menuItem, color: COLORS.danger }}
						onClick={action("statut", { action: "annuler" })}
					>
						Annuler la couverture…
					</button>
				</div>
			)}
		</div>
	)
}

const st = {
	empty: { padding: "4px 0 6px", color: COLORS.textMuted, fontSize: 13 },
	cov: { padding: "14px 0", borderBottom: `1px solid ${COLORS.border}` },
	covDerniere: { borderBottom: 0, paddingBottom: 4 },
	r1: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
	who: { fontWeight: 700, fontSize: 14.5 },
	src: { fontSize: 12.5, color: COLORS.textMuted },
	plafond: { marginLeft: "auto", fontSize: 13, color: COLORS.textMuted },
	r2: {
		marginTop: 4,
		fontSize: 12.5,
		color: COLORS.textMuted,
		display: "flex",
		gap: 12,
		flexWrap: "wrap",
	},
	r3: {
		marginTop: 10,
		display: "flex",
		alignItems: "center",
		gap: 14,
		flexWrap: "wrap",
	},
	jauge: { flex: 1, minWidth: 200 },
	bar: {
		height: 8,
		borderRadius: 99,
		backgroundColor: COLORS.bg,
		border: `1px solid ${COLORS.border}`,
		overflow: "hidden",
	},
	lab: { fontSize: 12.5, color: COLORS.textMuted, marginTop: 5 },
	valider: {
		marginTop: 10,
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "10px 12px",
		borderRadius: 10,
		backgroundColor: COLORS.warningLight,
		border: "1px solid #FDE68A",
		color: "#92400E",
		fontSize: 13,
		flexWrap: "wrap",
	},
	histo: { marginTop: 10, fontSize: 12.5, color: COLORS.textLight },
	menuBtn: {
		width: 30,
		height: 30,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		cursor: "pointer",
		color: COLORS.textMuted,
		fontFamily: "inherit",
	},
	menu: {
		position: "absolute",
		right: 0,
		top: 34,
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		boxShadow: "0 12px 28px rgba(28,25,23,.12)",
		minWidth: 250,
		padding: 6,
		zIndex: 5,
		textAlign: "left",
	},
	menuItem: {
		display: "block",
		width: "100%",
		textAlign: "left",
		border: 0,
		backgroundColor: "transparent",
		padding: "8px 10px",
		borderRadius: 6,
		fontSize: 13,
		cursor: "pointer",
		color: COLORS.text,
		fontFamily: "inherit",
	},
	ordres: {
		marginTop: 14,
		paddingTop: 12,
		borderTop: `1px solid ${COLORS.border}`,
	},
	sousTitre: {
		...gs.statLabel,
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		marginBottom: 4,
	},
	compact: {
		marginTop: 4,
		marginBottom: 14,
		padding: "12px 14px",
		borderRadius: 12,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
	},
	compactTitre: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 10,
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
	},
}

export default CouverturesCarte
